import axiosInstance from '../AxiosInstance';

/**
 * Downloads a file from a blob response
 * @param blob - The blob data
 * @param filename - The filename for download
 */
export const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Generic export function for any endpoint
 * @param endpoint - API endpoint path
 * @param format - 'excel' or 'pdf'
 * @param startDate - Optional start date in YYYY-MM-DD format
 * @param endDate - Optional end date in YYYY-MM-DD format
 * @returns Promise with the blob data
 */
const exportData = async (
  endpoint: string,
  format: 'excel' | 'pdf',
  startDate?: string,
  endDate?: string
): Promise<Blob> => {
  const authToken = localStorage.getItem('authToken');

  if (!authToken) {
    throw new Error('Authentication required');
  }

  // Build query parameters
  const params: Record<string, string> = {
    format,
  };

  if (startDate) {
    params.startDate = startDate;
  }

  if (endDate) {
    params.endDate = endDate;
  }

  const response = await axiosInstance.get(endpoint, {
    params,
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
    responseType: 'blob', // Important for file downloads
  });

  return response.data;
};

/**
 * Exports users data in the specified format
 */
export const exportUsers = async (
  format: 'excel' | 'pdf',
  startDate?: string,
  endDate?: string
): Promise<Blob> => {
  return exportData('/export-users', format, startDate, endDate);
};

/**
 * Exports orders data in the specified format
 */
export const exportOrders = async (
  format: 'excel' | 'pdf',
  startDate?: string,
  endDate?: string
): Promise<Blob> => {
  return exportData('/order/export-orders', format, startDate, endDate);
};

/**
 * Exports milestone gifts data in the specified format
 */
export const exportMilestoneGifts = async (
  format: 'excel' | 'pdf',
  startDate?: string,
  endDate?: string
): Promise<Blob> => {
  return exportData('/order/export-unverified-conditional-gifts', format, startDate, endDate);
};

/**
 * Exports posthumous gifts data in the specified format
 */
export const exportPosthumousGifts = async (
  format: 'excel' | 'pdf',
  startDate?: string,
  endDate?: string
): Promise<Blob> => {
  return exportData('/order/export-unverified-post-mortem-gifts', format, startDate, endDate);
};

/**
 * Exports subscriptions data in the specified format
 */
export const exportSubscriptions = async (
  format: 'excel' | 'pdf',
  startDate?: string,
  endDate?: string
): Promise<Blob> => {
  return exportData('/subscriptions/export', format, startDate, endDate);
};

/**
 * Exports support tickets data in the specified format
 */
export const exportSupportTickets = async (
  format: 'excel' | 'pdf',
  startDate?: string,
  endDate?: string
): Promise<Blob> => {
  return exportData('/support/admin/export-support-tickets', format, startDate, endDate);
};

/**
 * Exports transactions data in the specified format
 */
export const exportTransactions = async (
  format: 'excel' | 'pdf',
  startDate?: string,
  endDate?: string
): Promise<Blob> => {
  return exportData('/transactions/export-transactions', format, startDate, endDate);
};

/**
 * Gets the appropriate filename based on module, format and date range
 * @param moduleName - Name of the module
 * @param format - 'excel' or 'pdf'
 * @param startDate - Optional start date
 * @param endDate - Optional end date
 * @returns Filename string
 */
export const getExportFilename = (
  moduleName: string,
  format: 'excel' | 'pdf',
  startDate?: string,
  endDate?: string
): string => {
  const today = new Date().toISOString().split('T')[0];
  const extension = format === 'excel' ? 'xlsx' : 'pdf';

  if (startDate && endDate) {
    return `${moduleName}_Report_${startDate}_to_${endDate}.${extension}`;
  } else if (startDate) {
    return `${moduleName}_Report_${startDate}_to_${today}.${extension}`;
  } else if (endDate) {
    const oneMonthBefore = new Date(endDate);
    oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);
    const start = oneMonthBefore.toISOString().split('T')[0];
    return `${moduleName}_Report_${start}_to_${endDate}.${extension}`;
  } else {
    return `${moduleName}_Report_${today}.${extension}`;
  }
};
